import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
} from '@mui/material';

/**
 * MuiAccordion element to represent scope containing messages templates:
 *
 * @param {Object} props - Object. Component's props object.
 * @param {React.JSX.Element} props.ParentTitleComponent - React Element. Accordion title component to render (ex. Chip, Typography).
 * @param {Array} props.items - Array. Accordion's children elements to render.
 * @param {React.JSX.Element} props.alternativeContent - React Element. Alternative content if you don't want to render a list of items.
 * @param {Function} props.listItemFunc - Callback. Function that renders a listItem.
 * @param {Boolean} props.open - Boolean. Flag indicating if the scope group's accordion is open.
 * @param {Function} props.onClick - Function. OnClick function to expand and collapse Accordion's content.
 * @param {Boolean} props.uncontrolled - Boolean. Flag indicating if the Accordion component is uncontrolled --> (can be opened multiple accordions together)
 *
 * @returns {React.JSX.Element}
 */

const EditorAccordion = function EditorAccordion({
  id,
  ParentTitleComponent = <div />,
  items = [],
  listItemFunc = () => <div />,
  open = false,
  onClick = () => {},
  uncontrolled = false,
  alternativeContent = null,
  expandIcon = null,
}) {
  const [expanded, setExpanded] = useState();

  const toggleExpanded = (e) => {
    e.stopPropagation();
    setExpanded(e.target.value);
  };

  return (
    <Accordion
      id={id}
      elevation={5}
      disableGutters
      sx={{
        mt: '8px',
        mb: '8px',
        '&::before': {
          display: 'none',
        },
      }}
      expanded={uncontrolled ? expanded : open}
      onChange={uncontrolled ? toggleExpanded : onClick}
    >
      <AccordionSummary
        sx={{
          height: '56px',
          borderBottom: (t) => `1px solid ${t.palette.divider}`,
        }}
        expandIcon={expandIcon}
      >
        {ParentTitleComponent}
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        {alternativeContent ?? (
          <List disablePadding>{items?.map(listItemFunc)}</List>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

EditorAccordion.propTypes = {
  id: PropTypes.string.isRequired,
  ParentTitleComponent: PropTypes.node,
  items: PropTypes.array,
  listItemFunc: PropTypes.func,
  open: PropTypes.bool,
  onClick: PropTypes.func,
  uncontrolled: PropTypes.bool,
  alternativeContent: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  expandIcon: PropTypes.object,
};
export default EditorAccordion;
